import { App } from "antd";
import dayjs from "dayjs";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    getApiError,
    postTaskAddDebtTaskThunk,
    postTaskAddDistributionPricesThunk,
    postTaskAddRejectTaskThunk,
    postTaskAddSaleTaskThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import {
    AnyTask,
    AssignmentResponseType,
    AssignmentType,
    OrderType,
    TaskRoute,
    UserRole,
} from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";
import { v7 } from "uuid";

import { ROUTES } from "@/constants";
import { ROLE_CHILDREN_MAP } from "@/constants/domain/managerAgentVocabulary";
import { ASSIGNMENT_VOCABULARY } from "@/constants/ui/assignment";
import { generateOrderId } from "@/domains/orders";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
    useProtectedUserRoleSelector,
} from "@/hooks/redux";
import { makeCommand } from "@/hooks/requestQueue/useRequestQueue";
import { enqueueRequestQueueItem } from "@/store/slices/offline";
import { addAdditionalOfflineTask } from "@/store/slices/work";
import { isManagerRole } from "@/utils/business/isManagerRole";

interface UseCreateTaskArgs {
    pointId: string;
    pointTasks: AnyTask[];
}

export const useCreateTask = ({ pointId, pointTasks }: UseCreateTaskArgs) => {
    const { notification } = App.useApp();

    const offlineMode = useOfflineModeSelector();
    const activeSchedule = useAppSelector((s) => s.manager.activeSchedule);
    const role = useProtectedUserRoleSelector();
    const id = useAppSelector((s) => s.user.id);

    const verifiedPerformer = isManagerRole(role)
        ? activeSchedule?.userId ?? ""
        : id;
    const realRoleSlug = isManagerRole(role)
        ? (ROLE_CHILDREN_MAP[role] as UserRole)
        : role;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [creating, setCreating] = useState<null | TaskRoute>(null);

    const displayErrorMessage = useCallback(
        (error: unknown) => {
            const { displayMessage } = getApiError(error);

            notification.error({
                title: "Не вдалося створити задачу",
                description: displayMessage,
            });
        },
        [notification],
    );

    const createCustomTask = useCallback(async () => {
        const localTaskId = v7();

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: localTaskId,
                    name: ASSIGNMENT_VOCABULARY[
                        AssignmentType.PricesDistribution
                    ].label,
                    context: {
                        route: TaskRoute.CustomTask,
                        description: "",
                        responseType: AssignmentResponseType.PhotoText,
                        files: [],
                        additionalData: {
                            assignmentType: AssignmentType.PricesDistribution,
                        },
                    },
                    completed: false,
                    targetId: pointId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: localTaskId,
                    metadata: {
                        pointId,
                        taskId: localTaskId,
                    },
                    commands: [
                        makeCommand({
                            type: "postTaskAddDistributionPricesThunk",
                            title: "Створення задачі",
                            params: {
                                targetId: pointId,
                                taskId: localTaskId,
                                performer: verifiedPerformer,
                                roleSlug: realRoleSlug,
                                targetDate: dayjs().format(
                                    backendDateFormat.date,
                                ),
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );

            navigate(
                buildRoutePath(ROUTES.AssignmentTask, {
                    pointId,
                    taskId: localTaskId,
                }),
            );
        } else {
            setCreating(TaskRoute.CustomTask);

            try {
                const response = await dispatch(
                    postTaskAddDistributionPricesThunk({
                        targetId: pointId,
                        taskId: localTaskId,
                        performer: verifiedPerformer,
                        roleSlug: realRoleSlug,
                    }),
                ).unwrap();

                const taskIdFromServer = response.result.id;

                navigate(
                    buildRoutePath(ROUTES.AssignmentTask, {
                        pointId,
                        taskId: taskIdFromServer,
                    }),
                );
            } catch (error) {
                displayErrorMessage(error);
            } finally {
                setCreating(null);
            }
        }
    }, [
        dispatch,
        displayErrorMessage,
        pointId,
        navigate,
        offlineMode,
        realRoleSlug,
        verifiedPerformer,
    ]);

    const createRejectTask = useCallback(async () => {
        const rejectCount = pointTasks.filter(
            (task) => task.context.route === TaskRoute.RejectProducts,
        ).length;

        let taskId = v7();
        const orderId = generateOrderId(OrderType.R, rejectCount + 1, pointId);

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: taskId,
                    name: `Заявка на повернення товарів №${rejectCount + 1}`,
                    context: {
                        route: TaskRoute.RejectProducts,
                        orderId,
                    },
                    completed: false,
                    targetId: pointId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: taskId,
                    metadata: {
                        pointId,
                        taskId: taskId,
                        orderId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: [
                        makeCommand({
                            type: "postOrderCreateByPoint",
                            title: "Створення заявки",
                            params: {
                                type: OrderType.R,
                                pointId,
                            },
                            options: {
                                ignoreErrorCodes: ["-32000"],
                            },
                            resolveOutputs: [
                                {
                                    replaceValue: orderId,
                                    extractPath: "result",
                                },
                            ],
                        }),
                        makeCommand({
                            type: "postTaskAddSaleTaskThunk",
                            title: "Створення задачі",
                            params: {
                                targetId: pointId,
                                taskId,
                                orderId,
                                performer: verifiedPerformer,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );
        } else {
            setCreating(TaskRoute.RejectProducts);

            try {
                const { result } = await dispatch(
                    postTaskAddRejectTaskThunk({
                        targetId: pointId,
                        performer: verifiedPerformer,
                    }),
                ).unwrap();

                taskId = result.id;
            } catch (error) {
                displayErrorMessage(error);

                return;
            } finally {
                setCreating(null);
            }
        }

        navigate(
            buildRoutePath(ROUTES.RejectProductsTask, {
                pointId,
                taskId,
            }),
        );
    }, [
        dispatch,
        displayErrorMessage,
        pointId,
        pointTasks,
        navigate,
        offlineMode,
        verifiedPerformer,
    ]);

    const createAdditionalSale = async () => {
        const salesCount = pointTasks.filter(
            (task) => task.context.route === TaskRoute.SaleAdditional,
        ).length;

        const localTaskId = v7();
        const orderId = generateOrderId(OrderType.H, salesCount + 1, pointId);

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: localTaskId,
                    name: `Продаж №${salesCount + 1}`,
                    context: {
                        route: TaskRoute.SaleAdditional,
                        orderId,
                    },
                    completed: false,
                    targetId: pointId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: localTaskId,
                    metadata: {
                        pointId,
                        taskId: localTaskId,
                        orderId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: [
                        makeCommand({
                            type: "postOrderCreateByPoint",
                            title: "Створення заявки",
                            params: {
                                pointId,
                                type: OrderType.H,
                            },
                            options: {
                                ignoreErrorCodes: ["-32000"],
                            },
                            resolveOutputs: [
                                {
                                    replaceValue: orderId,
                                    extractPath: "result",
                                },
                            ],
                        }),
                        makeCommand({
                            type: "postTaskAddSaleTaskThunk",
                            title: "Створення задачі",
                            params: {
                                targetId: pointId,
                                taskId: localTaskId,
                                orderId,
                                performer: verifiedPerformer,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );
        } else {
            try {
                setCreating(TaskRoute.SaleAdditional);

                await dispatch(
                    postTaskAddSaleTaskThunk({
                        targetId: pointId,
                        performer: verifiedPerformer,
                    }),
                ).unwrap();
            } catch (error) {
                displayErrorMessage(error);
            } finally {
                setCreating(null);
            }
        }
    };

    const createTakeDebtTask = useCallback(async () => {
        const localTaskId = v7();

        if (offlineMode) {
            dispatch(
                addAdditionalOfflineTask({
                    id: localTaskId,
                    name: "Забрати борг",
                    context: {
                        route: TaskRoute.TakeDebt,
                    },
                    completed: false,
                    targetId: pointId,
                }),
            );

            dispatch(
                enqueueRequestQueueItem({
                    id: localTaskId,
                    metadata: {
                        pointId,
                        taskId: localTaskId,
                    },
                    options: {
                        todayOnly: true,
                    },
                    commands: [
                        makeCommand({
                            type: "postTaskAddDebtTaskThunk",
                            title: "Створення задачі",
                            params: {
                                targetId: pointId,
                                taskId: localTaskId,
                                performer: verifiedPerformer,
                            },
                            options: {
                                ignoreErrorCodes: ["-32010"],
                            },
                        }),
                    ],
                }),
            );

            navigate(
                buildRoutePath(ROUTES.TakeDebtTask, {
                    pointId,
                    taskId: localTaskId,
                }),
            );
        } else {
            setCreating(TaskRoute.TakeDebt);

            try {
                const response = await dispatch(
                    postTaskAddDebtTaskThunk({
                        targetId: pointId,
                        taskId: localTaskId,
                        performer: verifiedPerformer,
                    }),
                ).unwrap();

                const taskIdFromServer = response.result.id;

                navigate(
                    buildRoutePath(ROUTES.TakeDebtTask, {
                        pointId,
                        taskId: taskIdFromServer,
                    }),
                );
            } catch (error) {
                displayErrorMessage(error);
            } finally {
                setCreating(null);
            }
        }
    }, [
        dispatch,
        displayErrorMessage,
        pointId,
        navigate,
        offlineMode,
        verifiedPerformer,
    ]);

    return {
        createAdditionalSale,
        createTakeDebtTask,
        createCustomTask,
        createRejectTask,
        creating,
    };
};
